import { isParameterTyped, simplifyType } from '@thinkalpha/language-services';
import type { OutputColumnWithMetadata } from '@thinkalpha/platform-ws-client/contracts/table.js';
import { stableHash } from '@thinkalpha/ui-core/lib/hashedName';
import { isNumericFieldType } from '@thinkalpha/ui-core/util/typeUtils';
import type { DisplaySettings } from '@thinkalpha/ui-types/contracts/display/settings';
import { TableClass } from '@thinkalpha/ui-types/contracts/workspace';

const TABLE_CLASS_COLUMN_ORDER: Partial<Record<TableClass, readonly string[]>> = {
    [TableClass.orders]: [
        'OrderKey',
        'Platform',
        'Account',
        'OwnerID',
        'LastPrice',
        'AskPrice',
        'BidPrice',
        'Flags',
        'TIF',
        'Status',
        'Side',
        'FilledShares',
        'RemainingShares',
        'Ticker',
        'OrigRouteSubID',
        'OrigRouteCompID',
        'RouteSubID',
        'RouteCompID',
        'AvgPx',
        'OrderPrice',
        'OrderQuantity',
        'OrderToken',
        'Symbol',
        'TransactTime',
        'TraderAccount',
    ],
    [TableClass.fills]: [
        'ExecutionKey',
        'Platform',
        'LiquidInd',
        'MatchPoint',
        'Side',
        'Account',
        'OwnerID',
        'RemainingShares',
        'AvgPx',
        'ExecMarket',
        'LastShares',
        'LastPx',
        'OrderPrice',
        'OrderQuantity',
        'OrderToken',
        'ExecID',
        'Symbol',
        'TransactTime',
        'TraderAccount',
        'ServiceType',
        'Liquidity',
        'OrderSide',
    ],
    [TableClass.platforms]: [
        'PositionKey',
        'Platform',
        'PnL',
        'OpenPnL',
        'ClosedPnL',
        'MarketValue',
        'Exposure',
        'LongExposure',
        'ShortExposure',
        'SharesTraded',
        'OpenOrders',
        'PendingSharesBuy',
        'PendingSharesSell',
        'PendingNotionalBuy',
        'PendingNotionalSell',
    ],
    [TableClass.accounts]: [
        'PositionKey',
        'AccountPropertyKey',
        'OwnerID',
        'Account',
        'UpdateTime',
        'Trader',
        'TotalPnL',
        'TotalavOpenPnL',
        'TotalavClosedPnL',
        'TotalMarketValue',
        'TotalExposure',
        'TotalLongExposure',
        'TotalShortExposure',
        'TotalSharesTraded',
        'TotalOpenOrders',
        'TotalPendSharesBuy',
        'TotalPendSharesSell',
        'PendingNotionalBuy',
        'PendingNotionalSell',
        'PendLongExposure',
        'PendShortExposure',
        'PnL_AccountEquity',
        'TotalEquity',
        'BuyingPower',
        'EquityPnLRatio',
    ],
    [TableClass.symbols]: [
        'PositionKey',
        'UpdateTime',
        'Symbol',
        'LastPrice',
        'TotalPnL',
        'TotalOpenShares',
        'TotalMarketValue',
        'TotalExposure',
        'TotalLongExposure',
        'TotalShortExposure',
        'TotalSharesTraded',
        'TotalOpenOrders',
        'TotalPendSharesBuy',
        'TotalPendSharesSell',
        'PendingNotionalBuy',
        'PendingNotionalSell',
    ],
    [TableClass.traders]: [
        'LastTime',
        'Trader',
        'TraderID',
        'PnL',
        'avOpenPnL',
        'avClosedPnL',
        'OpenShares',
        'MarketValue',
        'Exposure',
        'LongExposure',
        'ShortExposure',
        'SharesTraded',
        'OpenOrders',
        'PendSharesBuy',
        'PendSharesSell',
        'PendingNotionalBuy',
        'PendingNotionalSell',
    ],
    [TableClass.positions]: [
        'PositionPnLKey',
        'PositionKey',
        'Trader',
        'Name',
        'Symbol',
        'OpenShares',
        'RiskPnL',
        'avOpenPnL',
        'avClosedPnL',
        'avAvgPx',
        'ratio',
        'CrossedIn',
        'PendingSharesBuy',
        'PendingSharesSell&SShort',
        'MinSinceLast',
        'SharesTraded',
        'MarketValue',
        'TransactTime',
        'SODPosition',
        'ChangeQty',
        'Platform',
        'PnL_PositionEquity',
    ],
    [TableClass.joinedAccounts]: [
        'PositionKey',
        'BenchmarkAccount',
        'Symbol',
        'PnL',
        'ratio',
        'TransactTime',
        'avOpenPnL',
        'BenchmarkPnL',
        'avClosedPnL',
        'BenchmarkMarketValue',
        'Exposure',
        'BenchmarkOpenShares',
        'MarketValue',
        'BenchmarkSharesTraded',
        'OpenShares',
        'Trader',
        'SharesTraded',
        'BoughtNotional',
        'SoldNotional',
        'Account',
        'PendingSharesBuy',
        'PendingSharesSell&SShort',
        'TradedNotional',
        'SlippagePnL',
        'SlippageVolume',
        '%SlippagePnL',
        '%SlippageVolume',
        'SlippageMarketValue',
        'AbsSlippageMarketValue',
        '%SlippageMarketValue',
        'ShareSlippage',
        'SlipagePer1000',
    ],
    [TableClass.joinedPositions]: [
        'PositionKey',
        'BenchmarkAccount',
        'Symbol',
        'ratio',
        'TransactTime',
        'BenchmarkPnL',
        'BenchmarkMarketValue',
        'BenchmarkSharesTraded',
        'Trader',
        'PnL',
        'avOpenPnL',
        'avClosedPnL',
        'Exposure',
        'MarketValue',
        'SharesTraded',
        'BoughtNotional',
        'SoldNotional',
        'TradedNotional',
        'SlippagePnL',
        'SlippageVolume',
        '%SlippagePnL',
        '%SlippageVolume',
        'SlippageMarketValue',
        'AbsSlippageMarketValue',
        '%SlippageMarketValue',
    ],
};

const TABLE_CLASS_HIDDEN_COLUMNS: Partial<Record<TableClass, ReadonlySet<string>>> = {
    [TableClass.accounts]: new Set(['OwnerID']),
};

/** Columns whose legacy `ColumnPreference.disabled` locked visibility in the ag-grid tool panel. */
const TABLE_CLASS_LOCKED_VISIBILITY_COLUMNS: Partial<Record<TableClass, ReadonlySet<string>>> = {
    [TableClass.platforms]: new Set(['PositionKey']),
    [TableClass.accounts]: new Set(['PositionKey']),
    [TableClass.symbols]: new Set(['PositionKey']),
    [TableClass.positions]: new Set(['PositionKey']),
    [TableClass.joinedPositions]: new Set(['PositionKey']),
};

const ZERO_DECIMAL_FIELD_FRAGMENTS = [
    'openqty',
    'quantitytraded',
    'extadv20',
    'totalopenquantity',
    'totalquantitytraded',
    'totalopenorders',
    'pnl',
    'exposure',
    'notional',
    'shares',
    'qty',
    'sodposition',
    'longexposure',
    'shortexposure',
    'betaexposure',
    'volume',
    'marketvalue',
    'pendingquantitybuy',
    'pendingquantitysell',
    'pendingnotionalbuy',
    'pendingnotionalsell',
] as const;

const POSITIVE_COLOR = 'grid-token:green-medium';
const NEGATIVE_COLOR = 'grid-token:red-medium';
const SYMBOL_COLOR = 'grid-token:amber-medium';
const CSS_IDENTIFIER_UNSAFE = /[^\w-]/g;

export interface TableColumnDefaults {
    readonly displaySettings: DisplaySettings[];
    readonly name: string;
    readonly visible: boolean;
}

export function tableDefaultColumnOrder(tableClass: TableClass | null): readonly string[] | undefined {
    return tableClass === null ? undefined : TABLE_CLASS_COLUMN_ORDER[tableClass];
}

/** Mirrors the legacy `lockVisible` policy used by the Table widget's ag-grid columns panel. */
export function tableCanToggleColumnVisibility(columnId: string, tableClass: TableClass | null): boolean {
    return tableClass === null || !TABLE_CLASS_LOCKED_VISIBILITY_COLUMNS[tableClass]?.has(columnId);
}

function isNumericColumn(column: OutputColumnWithMetadata): boolean {
    return !isParameterTyped(column.dataType) && isNumericFieldType(simplifyType(column.dataType));
}

function isSignColoredColumn(columnId: string): boolean {
    return (
        columnId.includes('PnL') ||
        columnId.endsWith('Change') ||
        columnId.endsWith('%Chg') ||
        columnId.endsWith('Change_Open') ||
        columnId.endsWith('%Chg_Open') ||
        (columnId.endsWith('Px') && columnId !== 'avAvgPx')
    );
}

function displaySettingsId(columnId: string, kind: 'default' | 'negative' | 'positive'): string {
    const safeColumnId = columnId.replace(CSS_IDENTIFIER_UNSAFE, '_');
    const disambiguator = safeColumnId === columnId ? '' : `-${stableHash(columnId, { length: 8 })}`;
    return `table-${safeColumnId}${disambiguator}-${kind}`;
}

function columnDisplaySettings(column: OutputColumnWithMetadata): DisplaySettings[] {
    const displaySettings: DisplaySettings[] = [];
    const numeric = isNumericColumn(column);
    const normalizedId = column.id.trim().toLowerCase();
    const numberFormatting = numeric
        ? {
              locale: 'system' as const,
              ...(normalizedId === 'ratio'
                  ? { decimalAmount: { min: 4, max: 4 } }
                  : ZERO_DECIMAL_FIELD_FRAGMENTS.some((fragment) => normalizedId.includes(fragment))
                    ? { decimalAmount: { min: 0, max: 0 } }
                    : {}),
          }
        : undefined;
    const isSymbol = column.unit === 'symbol' || normalizedId === 'symbol';

    if (numberFormatting !== undefined || isSymbol) {
        displaySettings.push({
            id: displaySettingsId(column.id, 'default'),
            condition: { type: 'noop' },
            formatting: numberFormatting === undefined ? {} : { number: numberFormatting },
            styles: isSymbol ? { text: { color: SYMBOL_COLOR } } : {},
        });
    }
    if (numeric && isSignColoredColumn(column.id)) {
        displaySettings.push(
            {
                id: displaySettingsId(column.id, 'positive'),
                condition: { type: 'positive' },
                formatting: {},
                styles: { text: { color: POSITIVE_COLOR } },
            },
            {
                id: displaySettingsId(column.id, 'negative'),
                condition: { type: 'negative' },
                formatting: {},
                styles: { text: { color: NEGATIVE_COLOR } },
            },
        );
    }
    return displaySettings;
}

export function tableColumnDefaults(
    column: OutputColumnWithMetadata,
    tableClass: TableClass | null,
): TableColumnDefaults {
    const classOrder = tableDefaultColumnOrder(tableClass);
    const classHidden = tableClass === null ? undefined : TABLE_CLASS_HIDDEN_COLUMNS[tableClass];
    const visible =
        !column.id.includes('Key') &&
        (classOrder === undefined || classOrder.includes(column.id)) &&
        !classHidden?.has(column.id);

    return {
        displaySettings: columnDisplaySettings(column),
        name: column.id.startsWith('Total') ? column.id.slice('Total'.length) : column.id,
        visible,
    };
}
